import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };


  /**
   * Opens the dialog and loads the product details from the given URL.
   * @param {string} productPageUrl - The product page URL to fetch.
   * @param {string} productTitle - The product title.
   */
  async openProduct(productPageUrl, productTitle, cardEl = null) {
    this.setAttribute('data-premium', 'true');
    this.showDialog();

    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      // Show loading spinner using button-spinner animation styles
      modalContent.innerHTML = `
        <div class="quick-add-modal__loading" style="display: flex; justify-content: center; align-items: center; height: 300px; color: var(--brylo-espresso, #3c2d20);">
          <span class="loading-spinner" style="width: 32px; height: 32px; border: 2px solid rgba(76, 55, 52, 0.15); border-top-color: currentColor; border-radius: 50%; animation: button-spinner 0.6s linear infinite;"></span>
        </div>
      `;
    }

    try {
      // Construct a relative URL to prevent CORS errors in local development environment
      let fetchUrl = productPageUrl;
      try {
        const urlObj = new URL(productPageUrl, window.location.origin);
        fetchUrl = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.error('Error parsing product URL:', e);
      }

      const decodeHtml = (html) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
      };

      // Converts a multiline plain-text ingredients field into formatted HTML.
      // Lines matching "Label: content" are treated as sections; allergen sections
      // become numbered lists; everything else becomes a paragraph.
      const formatIngredientsContent = (rawText) => {
        if (!rawText) return '';
        const lines = rawText.replace(/\r\n?/g, '\n').split('\n').filter(l => l.trim());
        if (!lines.length) return `<p>${rawText}</p>`;

        // If there are no label patterns, fall back to <br>-separated text
        if (!lines.some(l => l.includes(':'))) {
          return `<p>${lines.join('<br>')}</p>`;
        }

        return lines.map(line => {
          const colonIdx = line.indexOf(':');
          if (colonIdx === -1) return `<p>${line}</p>`;

          const label = line.substring(0, colonIdx).trim();
          const content = line.substring(colonIdx + 1).trim();

          if (label.toLowerCase().includes('allergen')) {
            const items = content
              .replace(/,\s+and\s+/gi, ', ')
              .replace(/\s+and\s+/gi, ', ')
              .split(',')
              .map(s => s.replace(/\.$/, '').trim())
              .filter(Boolean);
            const listHtml = items.length
              ? `<ol class="sig-popup-allergen-list">${items.map(i => `<li>${i}</li>`).join('')}</ol>`
              : `<p>${content}</p>`;
            return `<p class="sig-popup-ing-label">${label}:</p>${listHtml}`;
          }

          return `<p class="sig-popup-ing-label">${label}:</p><p>${content}</p>`;
        }).join('');
      };

      let title = productTitle;
      let price = '';
      let imageUrl = '';
      let imageAlt = '';
      let collectionName = 'STANDARD COLLECTION';
      let descriptionHtml = '';
      let ingredientsHtml = 'Please contact us for detailed ingredient and allergen information for this product.';
      let storageHtml = 'Store in a cool, dry place away from direct sunlight. Best consumed within the recommended timeframe after purchase.';
      let piecesText = '';
      let variantId = '';
      let available = true;
      let badgeText = '';
      let allergensHtml = '';
      /** @type {Array<string | {url: string, alt?: string}>} */
      let productImages = [];

      if (cardEl) {
        title = cardEl.getAttribute('data-product-title') || title;
        price = cardEl.getAttribute('data-product-price') || '';
        imageUrl = cardEl.getAttribute('data-product-image') || '';
        imageAlt = cardEl.getAttribute('data-product-image-alt') || title;
        descriptionHtml = decodeHtml(cardEl.getAttribute('data-product-description') || '');
        badgeText = cardEl.getAttribute('data-product-badge') || '';
        piecesText = cardEl.getAttribute('data-product-pieces') || '';
        ingredientsHtml = formatIngredientsContent(decodeHtml(cardEl.getAttribute('data-product-ingredients') || ingredientsHtml));
        storageHtml = decodeHtml(cardEl.getAttribute('data-product-storage') || storageHtml);
        allergensHtml = decodeHtml(cardEl.getAttribute('data-product-allergens') || '');

        // Merge separate allergens metafield into the accordion as a formatted list
        if (allergensHtml) {
          const allergenItems = allergensHtml
            .replace(/,\s+and\s+/gi, ', ')
            .replace(/\s+and\s+/gi, ', ')
            .split(',')
            .map(s => s.replace(/\.$/, '').trim())
            .filter(Boolean);
          ingredientsHtml += `<p class="sig-popup-ing-label">Allergen list:</p>`;
          ingredientsHtml += allergenItems.length > 1
            ? `<ol class="sig-popup-allergen-list">${allergenItems.map(i => `<li>${i}</li>`).join('')}</ol>`
            : `<p>${allergensHtml}</p>`;
          allergensHtml = '';
        }

        const collectionAttr = cardEl.getAttribute('data-product-collection');
        if (collectionAttr) collectionName = collectionAttr;
        variantId = cardEl.getAttribute('data-variant-id') || '';
        available = cardEl.getAttribute('data-product-available') !== 'false';

        const imagesRaw = cardEl.getAttribute('data-product-images');
        if (imagesRaw) {
          try {
            productImages = JSON.parse(decodeURIComponent(imagesRaw));
          } catch (e) {
            try {
              productImages = JSON.parse(imagesRaw);
            } catch (err) {
              productImages = [];
            }
          }
        }
      } else {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const responseText = await response.text();

        const html = new DOMParser().parseFromString(responseText, 'text/html');

        title = html.querySelector('h1')?.textContent.trim() || productTitle;
        
        const priceEl = html.querySelector('product-price, .price, [data-testid="price-display"]');
        price = priceEl ? priceEl.textContent.trim() : '';

        const imgEl = html.querySelector('.product-information__media img, product-media img, .product-media-gallery img, .product-media img, img[src*="/products/"]');
        imageUrl = imgEl ? imgEl.src : '';
        imageAlt = imgEl ? imgEl.alt : title;

        const galleryImgEls = Array.from(html.querySelectorAll('.product-information__media img, product-media img, .product-media-gallery img, .product-gallery img, .product-media img, img[src*="/products/"]'));
        if (galleryImgEls.length > 0) {
          const seen = new Set();
          galleryImgEls.forEach(img => {
            if (img.src && !seen.has(img.src)) {
              seen.add(img.src);
              productImages.push({ url: img.src, alt: img.alt || title });
            }
          });
        }

        if (fetchUrl.includes('/collections/')) {
          const match = fetchUrl.match(/\/collections\/([^/]+)/);
          if (match && match[1]) {
            collectionName = match[1].replace(/-/g, ' ').toUpperCase();
          }
        }

        const accordions = Array.from(html.querySelectorAll('.brylo-accordion__item, accordion-row, .accordion__row'));
        accordions.forEach(item => {
          const labelEl = item.querySelector('.brylo-accordion__trigger-label, .accordion__title');
          const label = labelEl ? labelEl.textContent.trim().toLowerCase() : '';
          const contentEl = item.querySelector('.brylo-accordion__content, .accordion__content');
          const content = contentEl ? contentEl.innerHTML : '';
          if (content) {
            if (label.includes('ingredient')) {
              ingredientsHtml = content;
            } else if (label === 'storage') {
              storageHtml = content;
            }
          }
        });

        if (!descriptionHtml) {
          descriptionHtml = html.querySelector('.product-description, .product__description, .rte')?.innerHTML || '';
        }

        const optionInputs = html.querySelectorAll('.variant-picker input[type="radio"], variant-picker option');
        for (const input of optionInputs) {
          const text = input.value || input.textContent;
          if (text && (text.toLowerCase().includes('pieces') || text.toLowerCase().includes('pcs'))) {
            piecesText = text;
            break;
          }
        }

        const form = html.querySelector('form[action*="/cart/add"]');
        const idInput = form?.querySelector('[name="id"]');
        variantId = idInput ? idInput.value : '';
        available = form ? !form.querySelector('button[type="submit"]')?.hasAttribute('disabled') : true;
      }

      if (!Array.isArray(productImages) || productImages.length === 0) {
        if (imageUrl) {
          productImages = [{ url: imageUrl, alt: imageAlt || title }];
        }
      }

      // Extract configured WhatsApp Number from current page's footer or signatures-tabs if possible
      let whatsappNumber = '971553545160';
      const footerWaLink = document.querySelector('a[href*="wa.me"], a[href*="api.whatsapp.com"]');
      if (footerWaLink) {
        const match = footerWaLink.href.match(/(?:wa\.me|phone|phone=)(\d+)/);
        if (match && match[1]) {
          whatsappNumber = match[1];
        }
      }

      const domain = window.location.hostname;
      const fullUrl = `https://${domain}${fetchUrl}`;
      const piecesMsg = piecesText ? `${piecesText}, ` : '';
      const message = `Hello, I would like to order: ${title} (Quantity: 1, ${piecesMsg}${price}). Link: ${fullUrl}`;
      const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

      if (modalContent) {
        // Render custom premium popup markup
        modalContent.innerHTML = `
          <div class="premium-quick-add sig-popup-content">
            <div class="sig-popup-image-col">
              <div class="sig-popup-gallery" id="sigPopupGallery">
                <div class="sig-popup-gallery__track" id="sigPopupGalleryTrack" data-lenis-prevent>
                  ${productImages.map((imgObj, idx) => `
                    <div class="sig-popup-gallery__slide">
                      <img src="${typeof imgObj === 'string' ? imgObj : imgObj.url}" alt="${(typeof imgObj === 'object' && imgObj.alt) ? imgObj.alt : title}" class="sig-popup-image" draggable="false" width="800" height="1000" loading="${idx === 0 ? 'eager' : 'lazy'}">
                    </div>
                  `).join('')}
                </div>
                <button type="button" class="sig-popup-gallery__nav sig-popup-gallery__nav--prev" id="sigPopupGalleryPrev" aria-label="Previous image" style="${productImages.length > 1 ? 'display: flex;' : 'display: none;'}">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <button type="button" class="sig-popup-gallery__nav sig-popup-gallery__nav--next" id="sigPopupGalleryNext" aria-label="Next image" style="${productImages.length > 1 ? 'display: flex;' : 'display: none;'}">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
                <div class="sig-popup-gallery__dots" id="sigPopupGalleryDots" style="${productImages.length > 1 ? 'display: flex;' : 'display: none;'}">
                  ${productImages.length > 1 ? productImages.map((_, idx) => `
                    <button type="button" class="sig-popup-gallery__dot ${idx === 0 ? 'sig-popup-gallery__dot--active' : ''}" data-index="${idx}" aria-label="Go to slide ${idx + 1}"></button>
                  `).join('') : ''}
                </div>
              </div>
            </div>
            <div class="sig-popup-details-col" data-lenis-prevent>
              <span class="sig-popup-collection">${collectionName}</span>
              ${badgeText ? `<div class="sig-popup-badge">${badgeText}</div>` : ''}
              <h2 class="sig-popup-title">${title}</h2>
              <span class="sig-popup-price">${price}</span>
              ${piecesText ? `<span class="sig-popup-pieces">${piecesText}</span>` : ''}
              <div class="sig-popup-divider"></div>
              <div class="sig-popup-description">${descriptionHtml}</div>

              <div class="sig-popup-accordion">

                <div class="sig-popup-accordion-item">
                  <button type="button" class="sig-popup-accordion-trigger" aria-expanded="false" aria-controls="sigPopupAccIngBody">
                    <span class="sig-popup-accordion-title">Ingredients & allergens</span>
                    <span class="sig-popup-accordion-icon"></span>
                  </button>
                  <div class="sig-popup-accordion-body" id="sigPopupAccIngBody" aria-hidden="true">
                    <div style="overflow: hidden;">
                      <div class="sig-popup-accordion-content">${ingredientsHtml}</div>
                    </div>
                  </div>
                </div>

                <div class="sig-popup-accordion-item">
                  <button type="button" class="sig-popup-accordion-trigger" aria-expanded="false" aria-controls="sigPopupAccStorageBody">
                    <span class="sig-popup-accordion-title">Storage</span>
                    <span class="sig-popup-accordion-icon"></span>
                  </button>
                  <div class="sig-popup-accordion-body" id="sigPopupAccStorageBody" aria-hidden="true">
                    <div style="overflow: hidden;">
                      <div class="sig-popup-accordion-content">${storageHtml}</div>
                    </div>
                  </div>
                </div>
              </div>

              ${allergensHtml ? `<div class="sig-popup-allergen">${allergensHtml}</div>` : ''}

              <div class="sig-popup-qty-row">
                <button type="button" class="sig-popup-qty-btn" id="sigPopupQtyMinus" aria-label="Decrease quantity">&minus;</button>
                <input type="number" class="sig-popup-qty-input" id="sigPopupQtyInput" value="1" min="1" max="99" readonly>
                <button type="button" class="sig-popup-qty-btn" id="sigPopupQtyPlus" aria-label="Increase quantity">&plus;</button>
              </div>

              <form method="post" action="/cart/add" id="sigPopupCartForm">
                <input type="hidden" name="id" id="sigPopupVariantId" value="${variantId}">
                <input type="hidden" name="quantity" id="sigPopupQtyHidden" value="1">
                <button type="submit" class="sig-popup-add-btn" id="sigPopupAddBtn" ${available ? '' : 'disabled'}>${available ? 'ADD TO BAG' : 'SOLD OUT'}</button>
              </form>

              <a href="${whatsappLink}" class="sig-popup-whatsapp-btn" id="sigPopupWhatsappBtn" target="_blank" rel="noopener noreferrer">ORDER VIA WHATSAPP</a>
            </div>
          </div>
        `;

        // ── BIND CUSTOM EVENT LISTENERS AND BEHAVIORS ──
        
        // Setup Gallery Slider Controls
        const galleryTrack = modalContent.querySelector('#sigPopupGalleryTrack');
        const galleryPrev = modalContent.querySelector('#sigPopupGalleryPrev');
        const galleryNext = modalContent.querySelector('#sigPopupGalleryNext');
        const galleryDots = modalContent.querySelector('#sigPopupGalleryDots');

        if (galleryTrack && productImages.length > 1) {
          let currentGalleryIndex = 0;

          // Measure with getBoundingClientRect so sub-pixel widths (common on iOS at
          // non-integer device widths) don't accumulate a snap-point drift.
          const slideWidth = () => galleryTrack.getBoundingClientRect().width || galleryTrack.clientWidth;

          const updateActiveDot = (idx) => {
            if (galleryDots) {
              const dots = galleryDots.querySelectorAll('.sig-popup-gallery__dot');
              dots.forEach((d, i) => {
                d.classList.toggle('sig-popup-gallery__dot--active', i === idx);
              });
            }
          };

          const scrollToGalleryIndex = (idx, smooth = true) => {
            const width = slideWidth();
            currentGalleryIndex = idx;
            updateActiveDot(idx);
            if (!width) return;

            const left = idx * width;
            // iOS below 15.4 ignores `behavior` in scrollTo, so fall back to scrollLeft.
            if (smooth && typeof galleryTrack.scrollTo === 'function') {
              galleryTrack.scrollTo({ left, behavior: 'smooth' });
              // Safari occasionally drops the smooth scroll when the modal is still
              // animating in; make sure we land on the slide either way.
              setTimeout(() => {
                if (currentGalleryIndex === idx && Math.abs(galleryTrack.scrollLeft - left) > 2) {
                  galleryTrack.scrollLeft = left;
                }
              }, 500);
            } else {
              galleryTrack.scrollLeft = left;
            }
          };

          if (galleryPrev) {
            galleryPrev.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const prevIdx = currentGalleryIndex > 0 ? currentGalleryIndex - 1 : productImages.length - 1;
              scrollToGalleryIndex(prevIdx);
            });
          }

          if (galleryNext) {
            galleryNext.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const nextIdx = currentGalleryIndex < productImages.length - 1 ? currentGalleryIndex + 1 : 0;
              scrollToGalleryIndex(nextIdx);
            });
          }

          if (galleryDots) {
            const dots = galleryDots.querySelectorAll('.sig-popup-gallery__dot');
            dots.forEach((dot, idx) => {
              dot.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToGalleryIndex(idx);
              });
            });
          }

          const syncIndexFromScroll = () => {
            const width = slideWidth();
            if (width <= 0) return;
            const detectedIdx = Math.min(
              productImages.length - 1,
              Math.max(0, Math.round(galleryTrack.scrollLeft / width))
            );
            if (detectedIdx !== currentGalleryIndex) {
              currentGalleryIndex = detectedIdx;
              updateActiveDot(detectedIdx);
            }
          };

          // `scrollend` fires once momentum settles; the debounce is the fallback for
          // Safari, where the swipe would otherwise report intermediate positions.
          if ('onscrollend' in galleryTrack) {
            galleryTrack.addEventListener('scrollend', syncIndexFromScroll, { passive: true });
          } else {
            let scrollDebounce = null;
            galleryTrack.addEventListener(
              'scroll',
              () => {
                clearTimeout(scrollDebounce);
                scrollDebounce = setTimeout(syncIndexFromScroll, 120);
              },
              { passive: true }
            );
          }

          // The modal opens with an animation and the gallery can be measured at zero
          // (or at a stale) width. Re-align to the active slide whenever the track is
          // resized — rotation and the iOS URL bar collapsing both hit this.
          if (typeof ResizeObserver !== 'undefined') {
            let lastWidth = 0;
            let resizeRaf = null;
            const resizeObserver = new ResizeObserver(() => {
              const width = slideWidth();
              if (width <= 0 || width === lastWidth) return;
              lastWidth = width;
              cancelAnimationFrame(resizeRaf);
              resizeRaf = requestAnimationFrame(() => scrollToGalleryIndex(currentGalleryIndex, false));
            });
            resizeObserver.observe(galleryTrack);
          }

          galleryTrack.scrollLeft = 0;
        }
        const accTriggers = modalContent.querySelectorAll('.sig-popup-accordion-trigger');
        accTriggers.forEach(trigger => {
          trigger.addEventListener('click', () => {
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            const bodyId = trigger.getAttribute('aria-controls');
            const body = modalContent.querySelector(`#${bodyId}`);

            // Close other items
            accTriggers.forEach(t => {
              if (t !== trigger) {
                t.setAttribute('aria-expanded', 'false');
                const b = modalContent.querySelector(`#${t.getAttribute('aria-controls')}`);
                if (b) b.setAttribute('aria-hidden', 'true');
              }
            });

            // Toggle current
            trigger.setAttribute('aria-expanded', String(!isExpanded));
            if (body) body.setAttribute('aria-hidden', String(isExpanded));
          });
        });

        // Quantity selector controls
        const qtyMinus = modalContent.querySelector('#sigPopupQtyMinus');
        const qtyPlus = modalContent.querySelector('#sigPopupQtyPlus');
        const qtyInput = modalContent.querySelector('#sigPopupQtyInput');
        const qtyHidden = modalContent.querySelector('#sigPopupQtyHidden');
        const waBtn = modalContent.querySelector('#sigPopupWhatsappBtn');

        const updateQuickAddWhatsAppLink = () => {
          if (waBtn) {
            const currentQty = (qtyInput ? parseInt(qtyInput.value) : 1) || 1;
            const piecesMsg = piecesText ? `${piecesText}, ` : '';
            const msg = `Hello, I would like to order: ${title} (Quantity: ${currentQty}, ${piecesMsg}${price}). Link: ${fullUrl}`;
            waBtn.href = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`;
          }
        };

        if (qtyMinus && qtyInput && qtyHidden) {
          qtyMinus.addEventListener('click', () => {
            let val = parseInt(qtyInput.value) || 1;
            if (val > 1) {
              val--;
              qtyInput.value = val;
              qtyHidden.value = val;
              updateQuickAddWhatsAppLink();
            }
          });
        }

        if (qtyPlus && qtyInput && qtyHidden) {
          qtyPlus.addEventListener('click', () => {
            let val = parseInt(qtyInput.value) || 1;
            if (val < 99) {
              val++;
              qtyInput.value = val;
              qtyHidden.value = val;
              updateQuickAddWhatsAppLink();
            }
          });
        }

        if (waBtn) {
          waBtn.addEventListener('click', () => {
            updateQuickAddWhatsAppLink();
          });
        }

        // Ajax Cart Form submit
        const cartForm = modalContent.querySelector('#sigPopupCartForm');
        const addBtn = modalContent.querySelector('#sigPopupAddBtn');

        if (cartForm && addBtn) {
          cartForm.addEventListener('submit', (e) => {
            e.preventDefault();
            
            addBtn.setAttribute('disabled', 'disabled');
            const originalText = addBtn.textContent;
            addBtn.textContent = 'ADDING...';

            const formData = new FormData(cartForm);

            fetch('/cart/add.js', {
              method: 'POST',
              body: formData
            })
            .then(response => {
              if (!response.ok) throw new Error('Network response was not ok');
              return response.json();
            })
            .then(async (addedItem) => {
              // Fetch updated cart
              const cartRes = await fetch('/cart.js');
              const cart = await cartRes.json();

              // Dispatch cart update event for theme components
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'signatures-popup-form',
                  data: {
                    source: 'product-form-component',
                    itemCount: cart.item_count,
                    variantId: addedItem.variant_id.toString()
                  }
                }
              }));

              // Try to open theme cart drawer component
              const cartDrawer = document.querySelector('cart-drawer-component');
              if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }

              addBtn.textContent = 'ADDED!';
              
              setTimeout(() => {
                addBtn.removeAttribute('disabled');
                addBtn.textContent = originalText;
                this.closeDialog();
              }, 1000);
            })
            .catch(error => {
              console.error('Error adding to cart:', error);
              addBtn.textContent = 'ERROR';
              setTimeout(() => {
                addBtn.removeAttribute('disabled');
                addBtn.textContent = originalText;
              }, 2000);
            });
          });
        }
      } else {
        throw new Error('No product details content container found in modal');
      }
    } catch (error) {
      console.error('Error opening product in quick add:', error);
      if (modalContent) {
        modalContent.innerHTML = `<div style="padding: 40px 20px; text-align: center; color: var(--brylo-espresso, #3c2d20); font-family: sans-serif; font-size: 14px;">Error loading product details: ${error.message}. Please try again.</div>`;
      }
    }
  }

  #handleDialogClose = () => {
    this.removeAttribute('data-premium');
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
